import config from "../../config";
import localDay from "../CRM/lib/localDay";
import { patientSearchParams } from "../TreatmentPlans/lib/patientSearch";

// Mirrors the auth pattern used across the other controllers (TreatmentPlans).
function authHeader() {
  const user = JSON.parse(localStorage.getItem("user"));
  if (user && user.key) {
    return { Authorization: "Token  " + user.key };
  }
  return {};
}

/**
 * Cross-office FTC task list.
 * GET /clinics/ftc/tasks/?page=&page_size=&date=[&task_type=][&carrier=&carrier=][&office_id=][&assignee=][&priority=][&group=carrier]
 * `date` is the browser's calendar day: a follow-up an FTC scheduled ("check
 * again on") is back in the queue from that day on, and the server runs in UTC.
 * Flat mode returns { count, page, page_size, results: [...] }.
 * group=carrier returns { grouped: true, count, groups: [{ carrier_code, carrier_name, count, tasks: [...] }] }.
 * Returns { json, status }.
 */
export const getFTCTasks = async ({ page = 1, pageSize = 25, taskType, carriers, officeId, assigneeId, priority, group, includeTourDemo, skipDetect, scope } = {}) => {
  const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());

  let query = `?page=${page}&page_size=${pageSize}&date=${localDay()}`;
  if (scope) {
    // "mine": routed to this FTC plus unassigned in their offices; "all": the
    // whole pool. Absent, the server answers "all".
    query += `&scope=${encodeURIComponent(scope)}`;
  }
  if (skipDetect) {
    // Fast first paint: DB-driven request rows only, skip the live per-office
    // Sikka appointment/predetermination detection.
    query += `&detect=0`;
  }
  if (includeTourDemo) {
    query += `&include_tour_demo=1`;
  }
  if (taskType) {
    query += `&task_type=${encodeURIComponent(taskType)}`;
  }
  if (Array.isArray(carriers)) {
    carriers.filter(Boolean).forEach((code) => {
      query += `&carrier=${encodeURIComponent(code)}`;
    });
  }
  if (officeId) {
    query += `&office_id=${encodeURIComponent(officeId)}`;
  }
  if (assigneeId) {
    query += `&assignee=${encodeURIComponent(assigneeId)}`;
  }
  if (priority) {
    query += `&priority=${encodeURIComponent(priority)}`;
  }
  if (group) {
    query += `&group=${encodeURIComponent(group)}`;
  }

  const req = new Request(`${config.apiGateway.URL}/clinics/ftc/tasks/${query}`, {
    method: "GET",
    headers,
  });

  const response = await fetch(req);
  let json = null;
  try {
    json = await response.json();
  } catch (e) {
    json = null;
  }
  return { json, status: response.status };
};

/**
 * Cross-office patient search for the FTC Tasks page.
 * GET /clinics/ftc/patient-search/?page=&page_size=&(name|email|phone)=[&scope=tasks][&office_id=][&detect=0]
 *
 * Returns the same envelope as getFTCTasks — { count, page, page_size, results }
 * in the FTC task-row shape — plus { count_all, count_with_tasks } for the
 * "All Patients" / "Patients with Tasks" tab labels. Rows for patients with no
 * outstanding work carry an empty work_items and a null priority.
 *
 * Returns { json, status }.
 */
export const getFTCPatientSearch = async ({ search, scope, page = 1, pageSize = 25, officeId, skipDetect } = {}) => {
  const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());

  let query = `?page=${page}&page_size=${pageSize}`;
  // Same term-shape routing the Patients board uses.
  const params = patientSearchParams(search);
  Object.keys(params).forEach((key) => {
    if (params[key]) query += `&${key}=${encodeURIComponent(params[key])}`;
  });
  if (scope) {
    query += `&scope=${encodeURIComponent(scope)}`;
  }
  if (officeId) {
    query += `&office_id=${encodeURIComponent(officeId)}`;
  }
  if (skipDetect) {
    query += `&detect=0`;
  }

  const req = new Request(`${config.apiGateway.URL}/clinics/ftc/patient-search/${query}`, {
    method: "GET",
    headers,
  });

  const response = await fetch(req);
  let json = null;
  try {
    json = await response.json();
  } catch (e) {
    json = null;
  }
  return { json, status: response.status };
};

// ---------------------------------------------------------------------------
// FTC mail — the inbox the FTC Panel adds.
//
// Separate from the CRM's /crm/offices/<id>/... email endpoints on purpose:
// those are super-admin and take an office id from the caller, whereas these are
// scoped server-side to the caller's OWN mailbox and assignments. A shared
// controller would have invited passing an office id that the server is
// deliberately not willing to trust from this surface.
// ---------------------------------------------------------------------------
async function ftcReq(path, { method = "GET", body } = {}) {
  const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const res = await fetch(
    new Request(`${config.apiGateway.URL}/crm${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    }),
  );
  let json = null;
  try {
    json = await res.json();
  } catch (e) {
    json = null;
  }
  return { json, status: res.status };
}

/** The offices this FTC may write to, each with its permitted contacts. */
export const getFtcMailOffices = () => ftcReq("/ftc/offices/");

export const getFtcConversations = (officeId) =>
  ftcReq(`/ftc/conversations/${officeId ? `?office_id=${encodeURIComponent(officeId)}` : ""}`);

export const getFtcThread = (conversationId) =>
  ftcReq(`/ftc/messages/?conversation_id=${encodeURIComponent(conversationId)}`);

export const markFtcThreadRead = (conversationId) =>
  ftcReq("/ftc/messages/", { method: "POST", body: { conversation_id: conversationId } });

export const composeFtcEmail = (body) => ftcReq("/ftc/compose/", { method: "POST", body });

export const replyFtcEmail = (body) => ftcReq("/ftc/reply/", { method: "POST", body });

/** Records at `officeId` an FTC may attach to an email. `q` matches the patient name. */
export const searchFtcReferences = (officeId, kind, q) =>
  ftcReq(
    `/ftc/references/search/?office_id=${encodeURIComponent(officeId)}` +
      `&kind=${encodeURIComponent(kind)}${q ? `&q=${encodeURIComponent(q)}` : ""}`,
  );

// --- reports ---------------------------------------------------------------

export const getFtcReportPreview = (officeId, period) =>
  ftcReq(
    `/ftc/reports/preview/?office_id=${encodeURIComponent(officeId)}` +
      `&period=${encodeURIComponent(period || "monthly")}`,
  );

export const getFtcReportSchedule = (officeId) =>
  ftcReq(`/ftc/reports/schedule/?office_id=${encodeURIComponent(officeId)}`);

export const saveFtcReportSchedule = (body) =>
  ftcReq("/ftc/reports/schedule/", { method: "PUT", body });

export const sendFtcReport = (body) => ftcReq("/ftc/reports/send/", { method: "POST", body });
